import { WButton } from '@alice-health/wonderland-react'
import { useContext, useCallback, useMemo } from 'react'
import {
  FiltersContext,
  FiltersDispatchContext
} from '@/modules/accredited-establishments/contexts'
import { useDrawer, useIsScrolled } from '@/global/hooks'
import { PROVIDER_NAME_SEARCH_ID } from '@/modules/accredited-establishments/components/Filters'
import { MapPrivacyNotice } from '@/modules/accredited-establishments/components/MapPrivacyNotice'
import { useFilters } from '@/modules/accredited-establishments/hooks'
import { getMobileButtonsConfig } from './FloatingActionButtons.helpers'
import { motion, AnimatePresence } from 'framer-motion'
import { zIndex } from '@/global/constants/z-index'

const FloatingActionButtons = () => {
  const isScrolled = useIsScrolled()
  const {
    isOpen: isSearchOpen,
    open: openSearch,
    close: closeSearch
  } = useDrawer(PROVIDER_NAME_SEARCH_ID)
  const { updateProviderName } = useFilters()
  const { view, filterPresentation } = useContext(FiltersContext)
  const filterDispatch = useContext(FiltersDispatchContext)

  /* Closing drops the term: nothing else on the page would show it. */
  const onCloseSearch = useCallback(() => {
    updateProviderName('')
    closeSearch()
    // `updateProviderName` is rebuilt on every render of the provider
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeSearch])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const actions = useMemo(
    () => ({
      openMap: () => filterDispatch({ type: 'SET_VIEW', payload: 'map' }),
      openList: () => filterDispatch({ type: 'SET_VIEW', payload: 'list' }),
      collapseFilters: () =>
        filterDispatch({
          type: 'SET_FILTER_PRESENTATION',
          payload: 'collapsed'
        }),
      expandFilters: () =>
        filterDispatch({
          type: 'SET_FILTER_PRESENTATION',
          payload: 'expanded'
        }),
      scrollToTop
    }),
    [filterDispatch, scrollToTop]
  )

  const { firstButton: firstMobileButton, secondButton: secondMobileButton } =
    getMobileButtonsConfig({
      view,
      filterPresentation,
      actions
    })

  return (
    <>
      {/* Mobile Buttons */}
      <div
        data-testid='mobile-buttons'
        className={`fixed lg:hidden w-full bottom-0 left-0 px-gl-spacing-04 py-gl-spacing-03 flex justify-center items-center gap-gl-spacing-02 ${zIndex.accreditedEstablishments.floatingActionButtons}`}
      >
        {isSearchOpen ? (
          <WButton
            variant='primary'
            size='medium'
            icon='icClose'
            iconButton
            aria-label='Fechar busca'
            onClick={onCloseSearch}
          />
        ) : (
          <>
            <WButton
              variant='primary'
              size='medium'
              onClick={firstMobileButton.onClick}
            >
              {firstMobileButton.label}
            </WButton>

            {/* Search by name. Only in list view: the map pair is another one. */}
            {view === 'list' ? (
              <WButton
                variant='primary'
                size='medium'
                icon='icSearch'
                iconButton
                aria-label='Buscar'
                onClick={openSearch}
              />
            ) : null}

            {view === 'list' ? <MapPrivacyNotice position='top-end' /> : null}

            <AnimatePresence mode='wait'>
              {secondMobileButton ? (
                <motion.div
                  key='second-button'
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { ease: 'easeOut', duration: 0.4 }
                  }}
                  exit={{
                    opacity: 0,
                    y: 20,
                    transition: { ease: 'easeInOut', duration: 0.3 }
                  }}
                  layout
                >
                  <WButton
                    variant='primary'
                    size='medium'
                    icon={secondMobileButton.icon}
                    onClick={secondMobileButton.onClick}
                  >
                    {secondMobileButton.label}
                  </WButton>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Desktop Buttons */}
      <AnimatePresence>
        {isScrolled && view === 'list' ? (
          <motion.div
            data-testid='desktop-buttons'
            key='scroll-to-top-button'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: { ease: 'easeInOut', duration: 0.4 }
            }}
            exit={{
              opacity: 0,
              scale: 0.8,
              transition: { ease: 'easeInOut', duration: 0.3 }
            }}
            className={`hidden lg:block fixed bottom-6 right-6 ${zIndex.accreditedEstablishments.floatingActionButtons}`}
          >
            <WButton
              variant='primary'
              size='xlarge'
              iconButton
              icon='icArrowUp'
              onClick={scrollToTop}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}

export { FloatingActionButtons }
